import { ScrollArea } from '@mantine/core';
import { Button } from '@zorro/design-system/v2/Button';
import { Card } from '@zorro/design-system/v2/Card';
import { Code } from '@zorro/design-system/v2/Code';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Title } from '@zorro/design-system/v2/Title';
import { Group, Stack, Text } from '@zorro/web/design-system';
import { useMemo } from 'react';

import { FormFooter } from '../../index';
import { clearStorage, toggleDebugMode } from '../Analytics/AnalyticsUtils';
import { useSupportInformationForm } from './useSupportInformationForm';

type ShareSupportInformationOperationsFormProps = {
  onClose: () => void;
};

const ShareSupportInformationOperationsForm = ({
  onClose,
}: ShareSupportInformationOperationsFormProps) => {
  const { supportInformation, handleSubmit } = useSupportInformationForm({
    onSuccess: onClose,
  });

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Send support information"
        primaryButtonProps={{
          onClick: handleSubmit,
        }}
      />
    ),
    [handleSubmit]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack>
      <Card variant="white-border">
        <Stack gap="xs">
          <Title order={3}>General Information</Title>
          <Group gap="xs">
            <Text>Environment:</Text>
            <Code>{supportInformation.environment}</Code>
          </Group>
          <Group gap="xs">
            <Text>Page URL:</Text>
            <Code>{supportInformation.pageURL}</Code>
          </Group>
          <Group gap="xs">
            <Text>Session URL:</Text>
            <Code>{supportInformation.sessionURL}</Code>
          </Group>
        </Stack>
      </Card>
      <Card variant="white-border">
        <Stack gap="xs">
          <Title order={3}>User Details</Title>
          <Code block mah="15rem" className="fs-mask">
            <ScrollArea.Autosize>
              {JSON.stringify(supportInformation.userDetails, null, 2)}
            </ScrollArea.Autosize>
          </Code>
        </Stack>
      </Card>

      <Card variant="white-border">
        <Stack gap="xs">
          <Title order={3}>Dev Area</Title>
          <Group justify="space-between">
            <Group gap="xs">
              <Text>Analytics Debug Mode:</Text>
              <Code>
                {supportInformation.isAnalyticsDebugMode ? 'Yes' : 'No'}
              </Code>
            </Group>
            <Group gap="xs">
              <Button size="xs" variant="secondary" onClick={toggleDebugMode}>
                Toggle Debug Mode
              </Button>
              <Button size="xs" variant="secondary" onClick={clearStorage}>
                Clear Storage
              </Button>
            </Group>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
};

export { ShareSupportInformationOperationsForm };
